import React, { lazy, Suspense, useState } from 'react';
import { PlusIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import LookupSearchField from './tablemaint/LookupSearchField';
import type { LocationRecord } from './LocationEditDrawer';
import type { DupeLocation } from '../services/locations.service';
import type { LocationLookupRow } from '../pages/order-management/fixtures';
import quoteService from '../services/quote.service';

/**
 * Reusable "Location Map" field: map a record to a CMS location (cms.locations).
 *
 * Two paths, mirroring V1 tablemaint's location-map widget:
 *  1. MAP EXISTING — type-ahead search (via /quotes/location-lookup) that stores the
 *     chosen locations.recno and shows its name/city/state.
 *  2. ADD NEW — if the location doesn't exist yet, "Add New Location" opens the full
 *     LocationEditDrawer (create mode). Its built-in address dedup offers undelete-or-
 *     create; whichever location results (created or the undeleted existing one) flows
 *     its recno back here via onChange.
 *
 * Built to be dropped in anywhere a record needs a CMS-location mapping — the EDI
 * Locations maintenance drawer today, the EDI Orders mapping UI next. LocationEditDrawer
 * is lazy-loaded so its Google-Maps dependency isn't pulled into callers' bundles/tests
 * until the user actually opens the create drawer.
 */
const LocationEditDrawer = lazy(() =>
  import('./LocationEditDrawer').then((m) => ({ default: m.LocationEditDrawer })),
);

export interface LocationMapValue {
  recno: number; // 0 = unmapped
  name: string;
  city: string;
  state: string;
}

const EMPTY: LocationMapValue = { recno: 0, name: '', city: '', state: '' };

interface Props {
  value: LocationMapValue;
  onChange: (v: LocationMapValue) => void;
  label?: string;
  disabled?: boolean;
}

export const LocationMapField: React.FC<Props> = ({ value, onChange, label = 'Location Map', disabled }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingLocation, setEditingLocation] = useState<LocationRecord | null>(null);

  const openCreate = () => {
    setMode('create');
    setEditingLocation(null);
    setDrawerOpen(true);
  };

  const loc = [value.city, value.state].filter(Boolean).join(', ');

  return (
    <div>
      <LookupSearchField<LocationLookupRow>
        label={label}
        displayValue={value.name}
        // Typing (without picking) unlinks — the user must pick a result to re-map, or
        // use "Add New Location" if it doesn't exist.
        onType={(text) => onChange({ recno: 0, name: text, city: '', state: '' })}
        onSelect={(row) => onChange({ recno: row.locationcode, name: row.lname, city: row.city, state: row.state })}
        // Include address in the match — same-name locations are told apart by street.
        search={(q) => quoteService.lookupLocation(q, true)}
        renderItem={(row) => {
          const addr = [row.address1, row.address2].filter(Boolean).join(', ');
          return (
            <div className="flex flex-col">
              <span className="font-medium text-gray-800 dark:text-gray-100">{row.lname}</span>
              {addr && <span className="text-xs text-muted">{addr}</span>}
              <span className="text-xs text-muted">
                {[row.city, row.state].filter(Boolean).join(', ')} · #{row.locationcode}
                {row.cyalias ? ` · ${row.cyalias}` : ''}
              </span>
            </div>
          );
        }}
        itemValue={(row) => String(row.locationcode)}
        itemLabel={(row) => row.lname}
        disabled={disabled}
        placeholder="Search locations by name, address, alias, or #…"
      />

      <div className="mt-1.5 flex items-center gap-3 text-xs">
        {value.recno > 0 ? (
          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
            <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
            Mapped to #{value.recno}
            {loc ? ` — ${loc}` : ''}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(EMPTY)}
                title="Clear mapping"
                className="ml-1 text-gray-400 hover:text-red-500"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        ) : (
          <span className="text-muted">Not mapped to a CMS location</span>
        )}
        <button
          type="button"
          onClick={openCreate}
          disabled={disabled}
          className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <PlusIcon className="w-3.5 h-3.5" /> Add New Location
        </button>
      </div>

      {drawerOpen && (
        <Suspense fallback={null}>
          <LocationEditDrawer
            isOpen
            mode={mode}
            location={editingLocation}
            onClose={() => setDrawerOpen(false)}
            onSaved={(result) => {
              if (result?.id != null) {
                onChange({
                  recno: Number(result.id),
                  name: result.lname || '',
                  city: result.lcity || '',
                  state: result.lstate || '',
                });
              }
              setDrawerOpen(false);
            }}
            onEditExisting={(dupe: DupeLocation) => {
              // Dedup surfaced an existing (possibly deleted) location — switch the
              // drawer to edit/undelete it, then map its recno back on save. Mirrors
              // LocationsEditor's create→edit orchestration.
              setMode('edit');
              setEditingLocation(dupe as unknown as LocationRecord);
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

export default LocationMapField;
